import {
  LobbyPlayer,
  KartState,
  RaceResult,
  ItemType,
  TOTAL_LAPS,
  KartId,
  MapId,
} from '@shared/types';
import { config } from '@shared/config';
import { expandTrackPath } from '@shared/track';
import { ALL_MAPS } from '@shared/maps';

// ============================================================
// Screen IDs matching index.html overlay divs
// ============================================================

type Screen =
  | 'main-menu'
  | 'kart-select'
  | 'map-select'
  | 'mp-menu'
  | 'lobby'
  | 'countdown-overlay'
  | 'hud'
  | 'pause-overlay'
  | 'results';

// ============================================================
// Callbacks for user actions
// ============================================================

import { clientSettings, type ShadowQuality, type ResolutionScale, type ParticleDensity } from './ClientSettings';

export interface UICallbacks {
  onSoloStart: (name: string, kartId: KartId, mapId: MapId) => void;
  onCreateRoom: (name: string) => void;
  onJoinRoom: (name: string, roomCode: string) => void;
  onToggleReady: () => void;
  onStartGame: () => void;
  onBackToMenu: () => void;
  onPause?: () => void;
  onResume?: () => void;
  onVolumeChange?: (volume: number) => void;
}

// ============================================================
// Item display config: label and color for each ItemType
// ============================================================

const ITEM_DISPLAY: Record<string, { label: string; color: string }> = {
  [ItemType.SpeedBoost]: { label: 'BOOST', color: '#ffdd44' },
  [ItemType.Projectile]: { label: 'SHELL', color: '#ff4444' },
  [ItemType.GreenShell]: { label: '\u{1F7E2} SHELL', color: '#22cc44' },
  [ItemType.RedShell]: { label: '\u{1F534} SHELL', color: '#cc2222' },
  [ItemType.Banana]: { label: '\u{1F34C} BANANA', color: '#ddcc22' },
  [ItemType.TripleGreenShell]: { label: '\u{1F7E2}x3', color: '#22cc44' },
  [ItemType.TripleRedShell]: { label: '\u{1F534}x3', color: '#cc2222' },
  [ItemType.TripleBanana]: { label: '\u{1F34C}x3', color: '#ddcc22' },
  [ItemType.Lightning]: { label: '\u26A1 BOLT', color: '#ffffff' },
  [ItemType.Star]: { label: '\u2B50 STAR', color: '#ffcc00' },
  [ItemType.BlueShell]: { label: '\u{1F535} BLUE', color: '#2244cc' },
  [ItemType.Blooper]: { label: '\u{1F991} INK', color: '#aa44ff' },
};

// All item type values for roulette cycling
const ALL_ITEM_TYPES = Object.keys(ITEM_DISPLAY);

// ============================================================
// UI manager
// ============================================================

export class UI {
  private currentScreen: Screen = 'main-menu';
  private selectedKart: KartId | null = null;
  private selectedMap: MapId | null = null;
  private callbacks: UICallbacks;
  private flowMode: 'solo' | 'multiplayer' | null = null;
  private roomPollInterval: ReturnType<typeof setInterval> | null = null;
  private lobbyPingInterval: ReturnType<typeof setInterval> | null = null;
  private getLatency: (() => number) | null = null;
  private lastToastMessage = '';
  private lastToastTime = 0;

  // Roulette animation state
  private rouletteIndex = 0;
  private rouletteAccum = 0;

  // Leaderboard (Tab hold)
  private tabHeld = false;

  constructor(callbacks: UICallbacks) {
    this.callbacks = callbacks;
    this.bindEvents();
    this.renderMapPreviews();
    this.renderKartPreviews();
  }

  // ────────────────────────────────────────────────────────────
  // Event binding
  // ────────────────────────────────────────────────────────────

  private bindEvents(): void {
    // ── Main menu ──
    this.el('btn-solo').addEventListener('click', () => {
      const name = this.getPlayerName();
      if (!name) return;
      this.flowMode = 'solo';
      this.showScreen('kart-select');
    });

    this.el('btn-multiplayer').addEventListener('click', () => {
      const name = this.getPlayerName();
      if (!name) return;
      this.flowMode = 'multiplayer';
      this.showScreen('mp-menu');
    });

    // ── Kart selection ──
    const kartCards = document.querySelectorAll('#kart-select .select-card');
    kartCards.forEach((card) => {
      card.addEventListener('click', () => {
        const kartId = (card as HTMLElement).dataset.kart as KartId;
        if (!kartId) return;

        this.selectedKart = kartId;

        // Highlight selected card, deselect others
        kartCards.forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');

        // Auto-advance to map select after a brief visual pause
        setTimeout(() => this.showScreen('map-select'), 300);
      });
    });

    this.el('btn-kart-back').addEventListener('click', () => {
      this.showScreen('main-menu');
    });

    // ── Map selection ──
    const mapCards = document.querySelectorAll('#map-select .select-card');
    mapCards.forEach((card) => {
      card.addEventListener('click', () => {
        const mapId = (card as HTMLElement).dataset.map as MapId;
        if (!mapId) return;

        this.selectedMap = mapId;

        // Highlight selected card
        mapCards.forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');

        // Start solo game
        if (this.flowMode === 'solo' && this.selectedKart) {
          const name = this.getPlayerName();
          this.callbacks.onSoloStart(name, this.selectedKart, this.selectedMap);
        }
      });
    });

    this.el('btn-map-back').addEventListener('click', () => {
      this.showScreen('kart-select');
    });

    // ── Multiplayer menu ──
    this.el('btn-create').addEventListener('click', () => {
      const name = this.getPlayerName();
      if (!name) return;
      this.callbacks.onCreateRoom(name);
    });

    this.el('btn-join').addEventListener('click', () => {
      const name = this.getPlayerName();
      const codeInput = document.getElementById(
        'room-code-input'
      ) as HTMLInputElement;
      const code = codeInput.value.trim().toUpperCase();
      if (!name) return;
      if (!code) {
        codeInput.focus();
        return;
      }
      this.callbacks.onJoinRoom(name, code);
    });

    // Allow pressing Enter in the room code input to join
    (document.getElementById('room-code-input') as HTMLInputElement).addEventListener(
      'keydown',
      (e: KeyboardEvent) => {
        if (e.code === 'Enter') {
          this.el('btn-join').click();
        }
      }
    );

    this.el('btn-mp-back').addEventListener('click', () => {
      this.showScreen('main-menu');
    });

    // ── Lobby ──
    this.el('btn-copy-invite').addEventListener('click', () => {
      const roomCode = this.el('room-code').textContent || '';
      const url = `${location.origin}?join=${roomCode}`;
      const btn = this.el('btn-copy-invite');

      // Clipboard API requires HTTPS; fall back to execCommand on plain HTTP
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(url).then(() => {
          btn.textContent = 'Copied!';
          setTimeout(() => { btn.textContent = 'Copy Invite Link'; }, 2000);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = 'Copy Invite Link'; }, 2000);
      }
    });

    this.el('btn-ready').addEventListener('click', () => {
      this.callbacks.onToggleReady();
    });

    this.el('btn-start').addEventListener('click', () => {
      this.callbacks.onStartGame();
    });

    // ── Pause overlay ──
    this.el('btn-resume').addEventListener('click', () => {
      this.resumeGame();
    });

    this.el('btn-quit-menu').addEventListener('click', () => {
      this.callbacks.onBackToMenu();
    });

    // ── Volume slider ──
    const volumeSlider = this.el('volume-slider') as HTMLInputElement;
    const volumeValue = this.el('volume-value');
    volumeSlider.addEventListener('input', () => {
      const v = parseInt(volumeSlider.value, 10);
      volumeValue.textContent = `${v}%`;
      this.callbacks.onVolumeChange?.(v / 100);
    });

    // ── Settings toggle buttons ──
    this.bindSettingsButtons();

    // Escape key: toggle pause when in HUD or pause-overlay
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        if (this.currentScreen === 'hud') {
          this.pauseGame();
        } else if (this.currentScreen === 'pause-overlay') {
          this.resumeGame();
        }
      }
      // Tab key: show leaderboard while held
      if (e.code === 'Tab' && this.currentScreen === 'hud') {
        e.preventDefault();
        this.tabHeld = true;
        const lb = this.elOpt('leaderboard');
        if (lb) lb.style.display = '';
      }
    });

    window.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.code === 'Tab') {
        this.tabHeld = false;
        const lb = this.elOpt('leaderboard');
        if (lb) lb.style.display = 'none';
      }
    });

    // ── Results ──
    this.el('btn-back-menu').addEventListener('click', () => {
      this.callbacks.onBackToMenu();
    });
  }

  // ────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────

  /** Safe getElementById that throws if not found (catches html/id mismatches early) */
  private el(id: string): HTMLElement {
    const element = document.getElementById(id);
    if (!element) {
      throw new Error(`UI element #${id} not found in DOM`);
    }
    return element;
  }

  /** Safe getElementById that returns null if not found (for optional HUD elements) */
  private elOpt(id: string): HTMLElement | null {
    return document.getElementById(id);
  }

  private getPlayerName(): string {
    const input = document.getElementById('player-name') as HTMLInputElement;
    const name = input.value.trim();
    if (!name) {
      // Default name if empty
      return 'Player';
    }
    return name;
  }

  // ────────────────────────────────────────────────────────────
  // Screen management
  // ────────────────────────────────────────────────────────────

  showScreen(screen: Screen): void {
    // List of all overlay screen IDs
    const overlayIds = [
      'main-menu',
      'kart-select',
      'map-select',
      'mp-menu',
      'lobby',
      'countdown-overlay',
      'pause-overlay',
      'results',
    ];

    // Hide all overlays
    for (const id of overlayIds) {
      const el = document.getElementById(id);
      if (el) el.style.display = 'none';
    }

    // HUD is special (not flex, it's block)
    this.el('hud').style.display = 'none';

    // Show the requested screen
    if (screen === 'hud') {
      this.el('hud').style.display = 'block';
    } else {
      const el = document.getElementById(screen);
      if (el) el.style.display = 'flex';
    }

    this.currentScreen = screen;

    // Room browser polling
    if (screen === 'mp-menu') {
      this.startRoomPolling();
    } else {
      this.stopRoomPolling();
    }

    // Lobby ping polling
    if (screen === 'lobby') {
      this.startLobbyPing();
    } else {
      this.stopLobbyPing();
    }
  }

  getCurrentScreen(): Screen {
    return this.currentScreen;
  }

  /** Provide a getter so the UI can poll latency for the lobby ping display */
  setLatencyGetter(fn: () => number): void {
    this.getLatency = fn;
  }

  // ────────────────────────────────────────────────────────────
  // Lobby UI
  // ────────────────────────────────────────────────────────────

  showLobby(roomCode: string): void {
    this.el('room-code').textContent = roomCode;
    this.showScreen('lobby');
  }

  updateLobbyPlayers(players: LobbyPlayer[], myId: string): void {
    const list = this.el('player-list');
    list.innerHTML = players
      .map((p) => {
        const colorHex = '#' + p.color.toString(16).padStart(6, '0');
        const youTag = p.id === myId ? ' (You)' : '';
        const hostTag = p.isHost
          ? '<span class="player-host">HOST</span>'
          : '';
        const readyClass = p.ready ? 'player-ready' : 'player-not-ready';
        const readyText = p.ready ? 'Ready' : 'Not Ready';
        return `
          <div class="player-entry">
            <div class="player-color" style="background:${colorHex}"></div>
            <span class="player-name">${escapeHtml(p.name)}${youTag}</span>
            ${hostTag}
            <span class="${readyClass}">${readyText}</span>
          </div>
        `;
      })
      .join('');

    // Show/hide the start button (only the host sees it)
    const me = players.find((p) => p.id === myId);
    const isHost = me?.isHost ?? false;
    const allReady = players.every((p) => p.ready);
    const startBtn = this.el('btn-start') as HTMLButtonElement;

    startBtn.style.display = isHost ? 'block' : 'none';
    startBtn.disabled = !allReady && players.length > 1;
  }

  // ────────────────────────────────────────────────────────────
  // Pause
  // ────────────────────────────────────────────────────────────

  pauseGame(): void {
    this.showScreen('pause-overlay');
    this.callbacks.onPause?.();
  }

  resumeGame(): void {
    this.showScreen('hud');
    this.callbacks.onResume?.();
  }

  // ────────────────────────────────────────────────────────────
  // Countdown overlay
  // ────────────────────────────────────────────────────────────

  showCountdown(seconds: number): void {
    this.showScreen('countdown-overlay');
    this.el('countdown-number').textContent =
      seconds > 0 ? seconds.toString() : 'GO!';
  }

  // ────────────────────────────────────────────────────────────
  // HUD
  // ────────────────────────────────────────────────────────────

  showHUD(): void {
    this.showScreen('hud');
  }

  updateHUD(kart: KartState): void {
    // Speed: convert game units to a display "km/h" (10x multiplier for feel)
    const speedKmh = Math.round(Math.abs(kart.speed) * 10);
    this.el('hud-speed').textContent = `${speedKmh} km/h`;

    // Lap display (1-indexed, clamped to max)
    const lap = Math.min(kart.lap + 1, TOTAL_LAPS);
    this.el('hud-lap').textContent = `Lap ${lap}/${TOTAL_LAPS}`;

    // Position with ordinal suffix
    const pos = kart.position;
    const suffix = ordinalSuffix(pos);
    this.el('hud-position').textContent = `${pos}${suffix}`;

    // ── Coin counter ──
    const coinsEl = this.elOpt('hud-coins');
    if (coinsEl) {
      coinsEl.textContent = `\u{1FA99} ${kart.coins}`;
    }

    // ── Wrong-way warning ──
    const wrongWayEl = this.elOpt('hud-wrong-way');
    if (wrongWayEl) {
      wrongWayEl.style.display = kart.wrongWay ? 'block' : 'none';
    }

    // ── Slipstream indicator ──
    const slipEl = this.elOpt('hud-slipstream');
    if (slipEl) {
      slipEl.style.display = kart.slipstreamTimer > 0 ? 'block' : 'none';
    }

    // ── Item display (with roulette animation) ──
    const itemEl = this.el('hud-item');

    if (kart.rouletteTimer > 0) {
      // Item roulette: cycle through item names rapidly
      const cycleSpeed = 0.05; // seconds per item switch
      this.rouletteAccum += 0.016; // approximate frame time
      if (this.rouletteAccum >= cycleSpeed) {
        this.rouletteAccum -= cycleSpeed;
        this.rouletteIndex = (this.rouletteIndex + 1) % ALL_ITEM_TYPES.length;
      }
      const rouletteItem = ALL_ITEM_TYPES[this.rouletteIndex];
      const display = ITEM_DISPLAY[rouletteItem];
      if (display) {
        itemEl.textContent = display.label;
        itemEl.style.color = display.color;
      }
    } else if (kart.item !== null) {
      // Show the held item
      this.rouletteAccum = 0;
      const display = ITEM_DISPLAY[kart.item];
      if (display) {
        itemEl.textContent = display.label;
        itemEl.style.color = display.color;
      } else {
        // Fallback for unknown items
        itemEl.textContent = kart.item.toString().toUpperCase();
        itemEl.style.color = 'white';
      }
    } else {
      // No item
      this.rouletteAccum = 0;
      itemEl.textContent = '';
    }
  }

  updateFPS(fps: number): void {
    const el = this.el('hud-fps');
    el.textContent = `${fps} FPS`;
    if (fps >= 55) {
      el.style.color = '#44ff44';
    } else if (fps >= 30) {
      el.style.color = '#ffdd44';
    } else {
      el.style.color = '#ff4444';
    }
  }

  updateDebug(latency: number, _tick: number): void {
    const el = this.el('hud-debug');
    if (latency <= 0) {
      // Solo mode — hide ping
      el.style.display = 'none';
      return;
    }
    el.style.display = '';
    el.textContent = `${latency}ms`;
    // Color-code: green < 80, yellow < 150, red >= 150
    if (latency < 80) {
      el.style.color = '#44ff44';
    } else if (latency < 150) {
      el.style.color = '#ffdd44';
    } else {
      el.style.color = '#ff4444';
    }
  }

  // ────────────────────────────────────────────────────────────
  // Settings toggle buttons
  // ────────────────────────────────────────────────────────────

  private bindSettingsButtons(): void {
    const current = clientSettings.get();

    // Initialize button states from saved settings
    const groups = document.querySelectorAll<HTMLElement>('.setting-options');
    for (const group of groups) {
      const settingKey = group.dataset.setting as keyof typeof current;
      if (!settingKey) continue;

      const buttons = group.querySelectorAll<HTMLButtonElement>('button');
      for (const btn of buttons) {
        // Set initial active state
        if (btn.dataset.value === current[settingKey]) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }

        btn.addEventListener('click', () => {
          // Deactivate siblings, activate this one
          for (const b of buttons) b.classList.remove('active');
          btn.classList.add('active');

          const value = btn.dataset.value!;
          clientSettings.set({ [settingKey]: value });
        });
      }
    }
  }

  // ────────────────────────────────────────────────────────────
  // Kart preview icons (rendered once at startup)
  // ────────────────────────────────────────────────────────────

  private renderKartPreviews(): void {
    const kartConfigs: Record<string, { color: string; accent: string }> = {
      speed_demon: { color: '#e94560', accent: '#ff6b81' },
      road_hugger: { color: '#44bbff', accent: '#66ddff' },
    };

    for (const [kartId, cfg] of Object.entries(kartConfigs)) {
      const canvas = document.querySelector(
        `canvas[data-kart-preview="${kartId}"]`
      ) as HTMLCanvasElement | null;
      if (!canvas) continue;

      const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);

      // Center the drawing
      const cx = w / 2;
      const cy = h / 2;

      if (kartId === 'speed_demon') {
        this.drawSpeedDemonIcon(ctx, cx, cy, cfg.color, cfg.accent);
      } else {
        this.drawRoadHuggerIcon(ctx, cx, cy, cfg.color, cfg.accent);
      }
    }
  }

  private drawSpeedDemonIcon(
    ctx: CanvasRenderingContext2D, cx: number, cy: number,
    color: string, accent: string
  ): void {
    // Speed Demon: long, low, sleek — side view facing right
    const scale = 1.1;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 28 * scale, 42 * scale, 6 * scale, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wheels (dark circles)
    ctx.fillStyle = '#222';
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 2;
    for (const wx of [-28, 24]) {
      ctx.beginPath();
      ctx.arc(cx + wx * scale, cy + 18 * scale, 9 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Hub cap
      ctx.fillStyle = '#666';
      ctx.beginPath();
      ctx.arc(cx + wx * scale, cy + 18 * scale, 4 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#222';
    }

    // Main body (long sleek shape)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx - 38 * scale, cy + 8 * scale);   // rear bottom
    ctx.lineTo(cx + 42 * scale, cy + 8 * scale);    // front bottom
    ctx.lineTo(cx + 48 * scale, cy + 2 * scale);    // nose tip
    ctx.lineTo(cx + 38 * scale, cy - 4 * scale);    // front top
    ctx.lineTo(cx + 10 * scale, cy - 6 * scale);    // windshield base
    ctx.lineTo(cx - 4 * scale, cy - 18 * scale);    // cockpit top
    ctx.lineTo(cx - 18 * scale, cy - 16 * scale);   // rear cockpit
    ctx.lineTo(cx - 24 * scale, cy - 6 * scale);    // rear body top
    ctx.lineTo(cx - 38 * scale, cy + 2 * scale);    // rear end
    ctx.closePath();
    ctx.fill();

    // Windshield
    ctx.fillStyle = 'rgba(136, 187, 255, 0.6)';
    ctx.beginPath();
    ctx.moveTo(cx + 10 * scale, cy - 6 * scale);
    ctx.lineTo(cx + 30 * scale, cy - 4 * scale);
    ctx.lineTo(cx + 26 * scale, cy + 2 * scale);
    ctx.lineTo(cx - 2 * scale, cy - 14 * scale);
    ctx.closePath();
    ctx.fill();

    // Rear spoiler
    ctx.fillStyle = '#222';
    ctx.fillRect(cx - 36 * scale, cy - 24 * scale, 12 * scale, 3 * scale);
    // Spoiler supports
    ctx.fillRect(cx - 34 * scale, cy - 21 * scale, 2 * scale, 16 * scale);
    ctx.fillRect(cx - 28 * scale, cy - 21 * scale, 2 * scale, 16 * scale);

    // Speed streaks
    ctx.strokeStyle = accent;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.5;
    for (const yOff of [-2, 4, 10]) {
      ctx.beginPath();
      ctx.moveTo(cx - 50 * scale, cy + yOff * scale);
      ctx.lineTo(cx - 42 * scale, cy + yOff * scale);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  private drawRoadHuggerIcon(
    ctx: CanvasRenderingContext2D, cx: number, cy: number,
    color: string, accent: string
  ): void {
    // Road Hugger: compact, rounded — side view facing right
    const scale = 1.1;

    // Shadow
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.ellipse(cx, cy + 26 * scale, 36 * scale, 6 * scale, 0, 0, Math.PI * 2);
    ctx.fill();

    // Wheels (wider, grippier look)
    ctx.fillStyle = '#222';
    ctx.strokeStyle = '#444';
    ctx.lineWidth = 2;
    for (const wx of [-22, 22]) {
      ctx.beginPath();
      ctx.arc(cx + wx * scale, cy + 16 * scale, 10 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      // Tread pattern
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      for (let a = 0; a < Math.PI * 2; a += Math.PI / 3) {
        ctx.beginPath();
        ctx.moveTo(
          cx + wx * scale + Math.cos(a) * 6 * scale,
          cy + 16 * scale + Math.sin(a) * 6 * scale
        );
        ctx.lineTo(
          cx + wx * scale + Math.cos(a) * 10 * scale,
          cy + 16 * scale + Math.sin(a) * 10 * scale
        );
        ctx.stroke();
      }
      ctx.strokeStyle = '#444';
      ctx.lineWidth = 2;
      // Hub cap
      ctx.fillStyle = '#888';
      ctx.beginPath();
      ctx.arc(cx + wx * scale, cy + 16 * scale, 4 * scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#222';
    }

    // Main body (rounded, compact)
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx - 30 * scale, cy + 8 * scale);    // rear bottom
    ctx.lineTo(cx + 30 * scale, cy + 8 * scale);     // front bottom
    ctx.quadraticCurveTo(cx + 38 * scale, cy + 4 * scale, cx + 36 * scale, cy - 2 * scale); // rounded nose
    ctx.lineTo(cx + 14 * scale, cy - 6 * scale);     // windshield base
    ctx.quadraticCurveTo(cx + 4 * scale, cy - 22 * scale, cx - 10 * scale, cy - 20 * scale); // dome cockpit
    ctx.lineTo(cx - 22 * scale, cy - 10 * scale);    // rear roof
    ctx.quadraticCurveTo(cx - 32 * scale, cy - 4 * scale, cx - 30 * scale, cy + 8 * scale); // rear
    ctx.closePath();
    ctx.fill();

    // Windshield (larger, more rounded)
    ctx.fillStyle = 'rgba(136, 187, 255, 0.6)';
    ctx.beginPath();
    ctx.moveTo(cx + 14 * scale, cy - 6 * scale);
    ctx.lineTo(cx + 28 * scale, cy - 2 * scale);
    ctx.quadraticCurveTo(cx + 20 * scale, cy + 4 * scale, cx + 10 * scale, cy + 2 * scale);
    ctx.quadraticCurveTo(cx + 2 * scale, cy - 16 * scale, cx - 6 * scale, cy - 18 * scale);
    ctx.closePath();
    ctx.fill();

    // Grip stripes on side
    ctx.strokeStyle = accent;
    ctx.lineWidth = 2;
    ctx.globalAlpha = 0.6;
    for (let i = 0; i < 3; i++) {
      const x = cx - 6 * scale + i * 8 * scale;
      ctx.beginPath();
      ctx.moveTo(x, cy + 6 * scale);
      ctx.lineTo(x + 4 * scale, cy - 2 * scale);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // ────────────────────────────────────────────────────────────
  // Map preview minimaps (rendered once at startup)
  // ────────────────────────────────────────────────────────────

  private renderMapPreviews(): void {
    for (const mapDef of ALL_MAPS) {
      const canvas = document.querySelector(
        `canvas[data-map-preview="${mapDef.id}"]`
      ) as HTMLCanvasElement | null;
      if (!canvas) continue;

      const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      // Support high-DPI
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width;
      const h = canvas.height;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.scale(dpr, dpr);

      // Generate the track path from control points
      const path = expandTrackPath(mapDef.controlPoints);
      if (path.length < 2) continue;

      // Compute bounding box
      let minX = Infinity, maxX = -Infinity;
      let minZ = Infinity, maxZ = -Infinity;
      for (const p of path) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.z < minZ) minZ = p.z;
        if (p.z > maxZ) maxZ = p.z;
      }

      const rangeX = maxX - minX || 1;
      const rangeZ = maxZ - minZ || 1;
      const padding = 16;
      const drawW = w - padding * 2;
      const drawH = h - padding * 2;

      // Uniform scale (don't stretch)
      const scale = Math.min(drawW / rangeX, drawH / rangeZ);
      const cx = minX + rangeX / 2;
      const cz = minZ + rangeZ / 2;

      const toX = (wx: number) => (wx - cx) * scale + w / 2;
      const toY = (wz: number) => (wz - cz) * scale + h / 2;

      // Draw the road as a thick semi-transparent path
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = mapDef.roadWidth * scale * 0.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.beginPath();
      const step = Math.max(1, Math.floor(path.length / 400));
      ctx.moveTo(toX(path[0].x), toY(path[0].z));
      for (let i = step; i < path.length; i += step) {
        ctx.lineTo(toX(path[i].x), toY(path[i].z));
      }
      ctx.closePath();
      ctx.stroke();

      // Draw the center line as a thinner bright line
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(toX(path[0].x), toY(path[0].z));
      for (let i = step; i < path.length; i += step) {
        ctx.lineTo(toX(path[i].x), toY(path[i].z));
      }
      ctx.closePath();
      ctx.stroke();

      // Draw start/finish marker
      const startX = toX(path[0].x);
      const startY = toY(path[0].z);
      ctx.fillStyle = '#ffdd44';
      ctx.beginPath();
      ctx.arc(startX, startY, 4, 0, Math.PI * 2);
      ctx.fill();

      // Label
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('START', startX, startY - 8);
    }
  }

  // ────────────────────────────────────────────────────────────
  // Room browser polling
  // ────────────────────────────────────────────────────────────

  private startRoomPolling(): void {
    this.stopRoomPolling();
    this.fetchAndRenderRooms();
    this.roomPollInterval = setInterval(() => this.fetchAndRenderRooms(), 3000);
  }

  private stopRoomPolling(): void {
    if (this.roomPollInterval) {
      clearInterval(this.roomPollInterval);
      this.roomPollInterval = null;
    }
  }

  private async fetchAndRenderRooms(): Promise<void> {
    const list = document.getElementById('room-list');
    if (!list) return;

    try {
      const res = await fetch(`http://${location.hostname}:${config.network.serverPort}/rooms`);
      if (!res.ok) throw new Error('fetch failed');
      const rooms: Array<{ code: string; hostName: string; playerCount: number; maxPlayers: number }> = await res.json();

      if (rooms.length === 0) {
        list.innerHTML = '<div class="room-list-empty">No open rooms</div>';
        return;
      }

      list.innerHTML = rooms
        .map((r) => `
          <div class="room-entry">
            <span class="room-host">${escapeHtml(r.hostName)}'s room</span>
            <span class="room-players">${r.playerCount}/${r.maxPlayers}</span>
            <button data-room-code="${escapeHtml(r.code)}">Join</button>
          </div>
        `)
        .join('');

      // Bind join buttons
      list.querySelectorAll<HTMLButtonElement>('button[data-room-code]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const code = btn.dataset.roomCode!;
          const name = this.getPlayerName();
          this.callbacks.onJoinRoom(name, code);
        });
      });
    } catch {
      // Silently ignore fetch errors (server may not be running)
    }
  }

  // ────────────────────────────────────────────────────────────
  // Lobby ping display
  // ────────────────────────────────────────────────────────────

  private startLobbyPing(): void {
    this.stopLobbyPing();
    this.updateLobbyPing();
    this.lobbyPingInterval = setInterval(() => this.updateLobbyPing(), 1000);
  }

  private stopLobbyPing(): void {
    if (this.lobbyPingInterval) {
      clearInterval(this.lobbyPingInterval);
      this.lobbyPingInterval = null;
    }
  }

  private updateLobbyPing(): void {
    const el = document.getElementById('lobby-ping');
    if (!el || !this.getLatency) return;

    const latency = this.getLatency();
    if (latency <= 0) {
      el.textContent = '';
      return;
    }
    el.textContent = `${latency}ms`;
    if (latency < 80) {
      el.style.color = '#44ff44';
    } else if (latency < 150) {
      el.style.color = '#ffdd44';
    } else {
      el.style.color = '#ff4444';
    }
  }

  // ────────────────────────────────────────────────────────────
  // Results screen
  // ────────────────────────────────────────────────────────────

  showToast(message: string, durationMs = 3000): void {
    const now = Date.now();
    if (message === this.lastToastMessage && now - this.lastToastTime < 3000) {
      return;
    }
    this.lastToastMessage = message;
    this.lastToastTime = now;

    const container = document.getElementById('toast-container');
    if (!container) return;

    const el = document.createElement('div');
    el.className = 'toast';
    el.style.setProperty('--toast-duration', `${durationMs - 400}ms`);
    el.textContent = message;
    container.appendChild(el);

    setTimeout(() => el.remove(), durationMs);
  }

  /** Update the leaderboard overlay (called every frame when Tab is held) */
  updateLeaderboard(karts: KartState[], localPlayerId: string): void {
    if (!this.tabHeld) return;
    const list = this.elOpt('leaderboard-list');
    if (!list) return;

    // Sort by race position (1st, 2nd, ...)
    const sorted = [...karts].sort((a, b) => a.position - b.position);

    list.innerHTML = sorted
      .map((k) => {
        const isLocal = k.id === localPlayerId;
        const colorHex = '#' + k.color.toString(16).padStart(6, '0');
        const pos = k.position;
        const suffix = ordinalSuffix(pos);

        // Derive bot tier from bot ID (bot_0 = rank 0, etc.)
        let tierHtml = '';
        const botMatch = k.id.match(/^bot_(\d+)$/);
        if (botMatch) {
          const rank = parseInt(botMatch[1], 10);
          const { label, cls } = getBotTier(rank);
          tierHtml = `<span class="lb-tier ${cls}">${label}</span>`;
        }

        return `
          <div class="lb-entry${isLocal ? ' lb-local' : ''}">
            <span class="lb-pos">${pos}${suffix}</span>
            <span class="lb-color" style="background:${colorHex}"></span>
            <span class="lb-name">${escapeHtml(k.name)}${tierHtml}</span>
          </div>
        `;
      })
      .join('');
  }

  showResults(rankings: RaceResult[]): void {
    const list = this.el('results-list');
    list.innerHTML = rankings
      .map((r) => {
        const suffix = ordinalSuffix(r.position);
        const timeStr =
          r.time > 0 ? `${(r.time / 1000).toFixed(1)}s` : 'DNF';
        return `
          <div class="result-entry">
            <span class="result-pos">${r.position}${suffix}</span>
            <span class="result-name">${escapeHtml(r.playerName)}</span>
            <span class="result-time">${timeStr}</span>
          </div>
        `;
      })
      .join('');

    this.showScreen('results');
  }
}

// ============================================================
// Utility functions
// ============================================================

function ordinalSuffix(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function getBotTier(rank: number): { label: string; cls: string } {
  if (rank < 3) return { label: 'Elite', cls: 'lb-tier-elite' };
  if (rank < 6) return { label: 'Pro', cls: 'lb-tier-pro' };
  if (rank < 9) return { label: 'Amateur', cls: 'lb-tier-amateur' };
  return { label: 'Rookie', cls: 'lb-tier-rookie' };
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
